import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, getCurrentInstance } from 'vue';
import {
    LogisticsTopBar,
    StepNavigator,
    StepHeader,
    DataImportBox,
    SpreadsheetEditor,
    ParsingOverlay,
} from '@/components/views/Logistics';
import { LogisticsStep, SpreadsheetData, DeliveryRawRow } from '@/interfaces/logistics/LogisticsInterfaces';
import { parseCSVText, buildSpreadsheetData, DELIVERY_COLUMNS } from '@/helpers/logistics/LogisticsParserHelper';

// Proxy for accessing $notification

export default /*#__PURE__*/_defineComponent({
  __name: 'Montages',
  setup(__props) {

const { proxy } = getCurrentInstance()!;

// State
const currentStep = ref(0);
const rawData = ref('');
const parsedData = ref<DeliveryRawRow[]>([]);
const spreadsheetData = ref<SpreadsheetData | null>(null);
const isParsing = ref(false);
const isProcessing = ref(false);
const spreadsheetEditorRef = ref<InstanceType<typeof SpreadsheetEditor> | null>(null);

// Steps configuration
const steps = computed<LogisticsStep[]>(() => [
    {
        id: 0,
        title: 'Import',
        description: 'Paste data',
        isActive: currentStep.value === 0,
        isCompleted: currentStep.value > 0,
        canProceed: canProceed(0),
        proceedButtonText: 'Next',
        class: 'import-step',
        proceedIcon: 'arrow-right',
    },
    {
        id: 1,
        title: 'Edit',
        description: 'Review and fix',
        isActive: currentStep.value === 1,
        isCompleted: currentStep.value > 1,
        canProceed: canProceed(1),
        proceedButtonText: 'Next',
        class: 'import-step',
        proceedIcon: 'arrow-right',
    },
    {
        id: 2,
        title: 'Print',
        description: 'Review and print',
        isActive: currentStep.value === 2,
        isCompleted: currentStep.value > 1,
        canProceed: canProceed(2),
        proceedButtonText: 'Print',
        class: 'import-step',
        proceedIcon: 'printer',
    },
]);

// Computed properties
const currentStepSubtitle = computed(() => {
    if (currentStep.value === 0) {
        if (parsedData.value.length > 0) {
            return `${parsedData.value.length} rows ready`;
        }
        return 'Step 1 of 2';
    }
    return `${parsedData.value.length} rows`;
});

const canProceed = (stepIndex: number) => {
    switch (stepIndex) {
        case 0:
            return rawData.value.trim().length > 0;
        case 1:
            return parsedData.value.length > 0;
        case 2:
            return parsedData.value.length > 0;
        default:
            return false;
    }
};

// Methods
function handleDataPasted(data: string) {
    rawData.value = data;
}

async function handleNext() {
    switch (currentStep.value) {
        case 0:
            const firstStepRendered = await renderFirstStepResults();

            if (firstStepRendered) {
                currentStep.value++;
            }
            break;
        case 1:
            const secondStepRendered = buildFinalDataAndRenderOutput();

            if (secondStepRendered) {
                proxy.$notification.success({
                    message: 'Success',
                    description: 'Data processing complete! You can now review and print the montage list.',
                });
                currentStep.value++;
            }
            break;
    }
}

function handleBack() {
    if (currentStep.value > 0) {
        currentStep.value--;
    }
}

function handleStepClick(step: LogisticsStep, stepIndex: number) {
    if (step.isActive) {
        return;
    }

    if (step.isCompleted) {
        currentStep.value = stepIndex;
    }
}

async function renderFirstStepResults() {
    try {
        isParsing.value = true;
        const processedRawData = processRawData();

        if (processedRawData) {
            const fetchedData = await sendDataToBEForProcessing(processedRawData.data);

            if (fetchedData.length > 0) {
                parsedData.value = parseFetchedData(fetchedData);
                // TODO: Use MONTAGE_COLUMNS when defined
                spreadsheetData.value = buildSpreadsheetData(parsedData.value, DELIVERY_COLUMNS);
            }
        }

        return true;
    } catch (error) {
        console.error('Error processing data:', error);
        proxy.$notification.error({
            message: 'Error',
            description: 'An error occurred while processing the data. Please check your input and try again.',
        });
        return false;
    } finally {
        isParsing.value = false;
    }
}

function processRawData() {
    if (!rawData.value.trim()) {
        return;
    }

    const processedData = parseCSVText(rawData.value);

    if (!processedData.success) {
        throw new Error('No valid data found in the input. Please check the format and try again.');
    }

    return processedData;
}

async function sendDataToBEForProcessing(data: DeliveryRawRow[]): Promise<DeliveryRawRow[]> {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    return data;
}

function parseFetchedData(fetchedData: any): DeliveryRawRow[] {
    return fetchedData as DeliveryRawRow[];
}

function buildFinalDataAndRenderOutput() {
    console.log('Final data ready for export:', spreadsheetData.value);
    return true;
}

function handleCellChange(change: { text: string; row: number; col: number }) {
    console.log('Cell changed:', change);
}

function handleDataChange(data: SpreadsheetData | null) {
    console.log('Data changed:', data);
}

return { __sfc: true,proxy, currentStep, rawData, parsedData, spreadsheetData, isParsing, isProcessing, spreadsheetEditorRef, steps, currentStepSubtitle, canProceed, handleDataPasted, handleNext, handleBack, handleStepClick, renderFirstStepResults, processRawData, sendDataToBEForProcessing, parseFetchedData, buildFinalDataAndRenderOutput, handleCellChange, handleDataChange, LogisticsTopBar, StepNavigator, StepHeader, DataImportBox, SpreadsheetEditor, ParsingOverlay }
}

})